import download from 'downloadjs'
import { combineEpics, ofType } from 'redux-observable'
import { from, of, race, throwError } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators'
import { fromSocketEvent } from 'services/socket'
import { PARTICIPANTS_CSV_ERROR, PARTICIPANTS_CSV_READY } from 'services/socket/socketEvents'

import { addDaysToDate, addHours, customFormat } from 'utils/dateUtils'
import { extractErrorMessage } from 'utils/ErrorMessages'
import { xhrExtractFileName } from 'utils/helpers'

import { selectActualDefaultLanguage, selectPartner } from 'containers/App/selectors'
import { CAMPAIGN_TYPES_VALUES } from 'containers/AwarenessCenter/constants'
import { MAP_DELIVERY_VALUE } from 'containers/AwarenessCenter/WizardCampaign/steps/constants'
import { DEFAULT_SIMULATION_PROPERTIES } from 'containers/AwarenessCenter/WizardCampaign/WizardSimulationCampaign/constants'
import {
  DEFAULT_COLLECTION_PERIOD,
  DEFAULT_TRAINING_PROPERTIES,
} from 'containers/AwarenessCenter/WizardCampaign/WizardTrainingCampaign/constants'
import { shareReset } from 'containers/Share/actions'
import { selectMessage, selectSelectedProfilesProperties } from 'containers/Share/selectors'
import { addToast } from 'containers/Toasts/actions'

import { toTrainingParticipantsQuery } from 'components/pages/awareness-center/CampaignReport/adapters/campaignParticipantsAdapter'
import { formatAssignmentResendToast } from 'components/pages/awareness-center/CampaignReport/assignmentResendMessage'
import { reportToast } from 'components/pages/awareness-center/CampaignReport/components/ReportToast'
import { formatExtendCollectionPeriodToast } from 'components/pages/awareness-center/CampaignReport/extendCollectionPeriodMessage'
import { formatExtendDeadlineToast } from 'components/pages/awareness-center/CampaignReport/extendDeadlineMessage'
import {
  formatManualCompletionRevertToast,
  formatManualCompletionToast,
} from 'components/pages/awareness-center/CampaignReport/manualCompletionMessage'
import { formatNotifyManagersToast } from 'components/pages/awareness-center/CampaignReport/notifyManagersMessage'
import { formatParticipantExclusionToast } from 'components/pages/awareness-center/CampaignReport/participantExclusionMessage'
import { toPausedUntilIso } from 'components/pages/awareness-center/CampaignReport/reminderPauseBounds'
import {
  formatPauseRemindersToast,
  formatResumeRemindersToast,
} from 'components/pages/awareness-center/CampaignReport/reminderPauseMessage'
import { formatResetDeadlineToast } from 'components/pages/awareness-center/CampaignReport/resetDeadlineMessage'
import { formatResetProgressToast } from 'components/pages/awareness-center/CampaignReport/resetProgressMessage'
import { formatSendReminderToast } from 'components/pages/awareness-center/CampaignReport/sendReminderMessage'
import { formatStatusReminderToast } from 'components/pages/awareness-center/CampaignReport/statusReminderMessage'

import {
  CREATE_ASSIGNMENT_RESENDS_ASYNC,
  CREATE_MANUAL_COMPLETIONS_ASYNC,
  CREATE_PARTICIPANT_EXCLUSIONS_ASYNC,
  DOWNLOAD_PARTICIPANTS_CSV_ASYNC,
  DUPLICATE_CAMPAIGN_ASYNC,
  EMERGENCY_STOP_REQUEST_ASYNC,
  END_DATE_INTERVAL,
  EXTEND_COLLECTION_PERIOD_ASYNC,
  EXTEND_PARTICIPANT_DEADLINES_ASYNC,
  GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC,
  GET_CAMPAIGN_DATA_ASYNC,
  GET_CAMPAIGN_DEPARTMENT_OUTCOMES_ASYNC,
  GET_CAMPAIGN_FINAL_RESULTS_ASYNC,
  GET_CAMPAIGN_HEALTH_ASYNC,
  GET_CAMPAIGN_INFO_ASYNC,
  GET_CAMPAIGN_PARTICIPANTS_ASYNC,
  GET_CAMPAIGN_SIMULATION_FUNNEL_ASYNC,
  GET_CAMPAIGN_SIMULATION_OVERVIEW_ASYNC,
  GET_CAMPAIGN_STATUS_COUNTS_ASYNC,
  GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC,
  GET_REPORT_PARTICIPANTS_LIST_ASYNC,
  GET_REPORT_SUMMARY_ASYNC,
  HOURS_TO_START_CAMPAIGN,
  NOTIFY_MANAGERS_ASYNC,
  PAUSE_PARTICIPANT_REMINDERS_ASYNC,
  RESEND_TRAINING_REQUEST_ASYNC,
  RESET_PARTICIPANT_DEADLINES_ASYNC,
  RESET_TRAINING_PROGRESS_ASYNC,
  RESUME_PARTICIPANT_REMINDERS_ASYNC,
  REVERT_MANUAL_COMPLETIONS_ASYNC,
  SAVE_DRAFT_ASYNC,
  SAVE_MANUAL_TRAINING_RESULTS_ASYNC,
  SEGMENT_FILTER_CHANGE,
  SEND_REMINDERS_ASYNC,
  SEND_STATUS_REMINDERS_ASYNC,
  SHARE_ACTIONS,
  SHARE_CSV_REPORT_ASYNC,
} from './constants'
import {
  selectData,
  selectFilters,
  selectSegment,
  selectSelectionData,
  selectTrainingResultsModalStatus,
  selectTrainingResultsPostponedDate,
} from './selectors'

const getCampaignDraft = (campaignData) => {
  const CAMPAIGNS_MAP = {
    [CAMPAIGN_TYPES_VALUES.SIMULATION]: {
      endDate: customFormat(addDaysToDate(new Date(), END_DATE_INTERVAL), 'yyyy-MM-dd'),
      closeTime: `${customFormat(
        addDaysToDate(new Date(), END_DATE_INTERVAL + DEFAULT_COLLECTION_PERIOD),
        "yyyy-MM-dd'T'HH:mm"
      )}Z`,
      scenariosIds: campaignData.scenarios?.map((scenario) => scenario.id),
      landingPageId: campaignData.landing_page?.id,
      notifications: campaignData.notifications,
      ...DEFAULT_SIMULATION_PROPERTIES,
    },
    [CAMPAIGN_TYPES_VALUES.TRAINING]: {
      trainingCampaignData: {
        subject: campaignData.training_campaign_data?.subject,
        message: campaignData.training_campaign_data?.message,
        fromEmail: campaignData.training_campaign_data?.fromEmail,
      },
      closeTime: `${customFormat(addDaysToDate(new Date(), 21), "yyyy-MM-dd'T'HH:mm")}Z`,
      ...DEFAULT_TRAINING_PROPERTIES,
    },
  }

  return CAMPAIGNS_MAP[campaignData.flow_type]
}

const emergencyStopEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(EMERGENCY_STOP_REQUEST_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter.endCampaign(action.payload.id).pipe(
        mergeMap(() =>
          of(
            EMERGENCY_STOP_REQUEST_ASYNC.onSuccess(),
            // the report unmounts on the redirect that follows, so its own toast
            // cannot carry this - the same card goes through ToastsContainer instead
            addToast(reportToast('Campaign stopped.'))
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(EMERGENCY_STOP_REQUEST_ASYNC.onError, error),
            addToast({ message: extractErrorMessage(error), type: 'error' })
          )
        ),
        takeUntil(action$.pipe(ofType(EMERGENCY_STOP_REQUEST_ASYNC.CANCEL)))
      )
    )
  )

const resendTrainingEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RESEND_TRAINING_REQUEST_ASYNC.INITIATE),
    mergeMap((action) => {
      const selectionData = selectSelectionData(state$.value)

      return api.awarenessCenter
        .resendTraining(action.payload.id, {
          companyFilter: state$.value.partnerOptions.selectedCompany?.value,
          partner: !!selectPartner(state$.value),
          search: selectFilters(state$.value).search,
          segments: selectSegment(state$.value),
          simulationResultTypes: selectFilters(state$.value).simulationResultTypes,
          trainingResultTypes: selectFilters(state$.value).trainingResultTypes,
          departments: selectFilters(state$.value).departments,
          trainingScores: selectFilters(state$.value).trainingResults,
          awarenessLevels: selectFilters(state$.value).levels,
          scenarios: selectFilters(state$.value).scenarios,
          selectionData: selectionData.allItems
            ? {
                allItems: true,
                excludeIds: selectionData.selectionIds,
              }
            : {
                allItems: false,
                includeIds: selectionData.selectionIds,
                excludeIds: [],
              },
        })
        .pipe(
          switchMap(() => of(addToast({ message: 'Training emails resent successfully!' }))),
          catchError((error) =>
            of(
              epicsErrorHandler(RESEND_TRAINING_REQUEST_ASYNC.onError, error),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(RESEND_TRAINING_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

const getReportParticipantsListEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_REPORT_PARTICIPANTS_LIST_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getReportParticipantsList({
          companyFilter: state$.value.partnerOptions.selectedCompany?.value,
          partner: !!selectPartner(state$.value),
          reportId: action.payload.params.reportId,
          page: action.payload.params.page,
          pageSize: action.payload.params.pageSize,
          search: selectFilters(state$.value).search,
          simulationResultTypes: selectFilters(state$.value).simulationResultTypes,
          trainingResultTypes: selectFilters(state$.value).trainingResultTypes,
          departments: selectFilters(state$.value).departments,
          trainingScores: selectFilters(state$.value).trainingResults,
          awarenessLevels: selectFilters(state$.value).levels,
          scenarios: selectFilters(state$.value).scenarios,
          segments: selectSegment(state$.value),
          trainingIds: selectFilters(state$.value).trainingIds,
        })
        .pipe(
          switchMap((response) =>
            of(
              GET_REPORT_PARTICIPANTS_LIST_ASYNC.onSuccess({
                data: response,
              })
            )
          ),
          catchError((error) =>
            of(epicsErrorHandler(GET_REPORT_PARTICIPANTS_LIST_ASYNC.onError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(GET_REPORT_PARTICIPANTS_LIST_ASYNC.CANCEL)))
        )
    )
  )

const getReportSummaryEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_REPORT_SUMMARY_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .getReportSummary(
          state$.value.partnerOptions.selectedCompany?.value,
          !!selectPartner(state$.value),
          action.payload.reportId,
          selectSegment(state$.value)
        )
        .pipe(
          mergeMap((response) => of(GET_REPORT_SUMMARY_ASYNC.onSuccess({ data: response }))),
          catchError((error) =>
            of(epicsErrorHandler(GET_REPORT_SUMMARY_ASYNC.onError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(GET_REPORT_SUMMARY_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignHealthEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_HEALTH_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignHealth(state$.value.partnerOptions.selectedCompany?.value, action.payload.id)
        .pipe(
          map((response) => GET_CAMPAIGN_HEALTH_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_HEALTH_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_HEALTH_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignInfoEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_INFO_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignInfo(state$.value.partnerOptions.selectedCompany?.value, action.payload.id)
        .pipe(
          map((response) => GET_CAMPAIGN_INFO_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_INFO_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_INFO_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignSimulationOverviewEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_SIMULATION_OVERVIEW_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignSimulationOverview(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id
        )
        .pipe(
          map((response) => GET_CAMPAIGN_SIMULATION_OVERVIEW_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_SIMULATION_OVERVIEW_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_SIMULATION_OVERVIEW_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignSimulationFunnelEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_SIMULATION_FUNNEL_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignSimulationFunnel(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id
        )
        .pipe(
          map((response) => GET_CAMPAIGN_SIMULATION_FUNNEL_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_SIMULATION_FUNNEL_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_SIMULATION_FUNNEL_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignDepartmentOutcomesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_DEPARTMENT_OUTCOMES_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignDepartmentOutcomes(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id
        )
        .pipe(
          map((response) => GET_CAMPAIGN_DEPARTMENT_OUTCOMES_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_DEPARTMENT_OUTCOMES_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_DEPARTMENT_OUTCOMES_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignFinalResultsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_FINAL_RESULTS_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignFinalResults(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id
        )
        .pipe(
          map((response) => GET_CAMPAIGN_FINAL_RESULTS_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_FINAL_RESULTS_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_FINAL_RESULTS_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignParticipantsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_PARTICIPANTS_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignTrainingParticipants(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          toTrainingParticipantsQuery(action.payload)
        )
        .pipe(
          map((response) => GET_CAMPAIGN_PARTICIPANTS_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_PARTICIPANTS_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_PARTICIPANTS_ASYNC.CANCEL)))
        )
    )
  )

const createAssignmentResendsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_ASSIGNMENT_RESENDS_ASYNC.INITIATE),
    mergeMap((action) => {
      const includeIds = action.payload.includeIds
      const body = includeIds == null ? {} : { includeIds }

      return api.awarenessCenter
        .createAssignmentResends(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          body
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              CREATE_ASSIGNMENT_RESENDS_ASYNC.onSuccess({ data: response }),
              addToast(reportToast(formatAssignmentResendToast(response)))
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(CREATE_ASSIGNMENT_RESENDS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(CREATE_ASSIGNMENT_RESENDS_ASYNC.CANCEL)))
        )
    })
  )

const extendCollectionPeriodEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(EXTEND_COLLECTION_PERIOD_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .extendCollectionPeriod(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          { collectingUntil: action.payload.collectingUntil }
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              EXTEND_COLLECTION_PERIOD_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(
                  formatExtendCollectionPeriodToast({
                    collectingUntil: response?.collectingUntil || action.payload.collectingUntil,
                  })
                )
              ),
              GET_CAMPAIGN_INFO_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_HEALTH_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC.initiate({ id: action.payload.id })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(EXTEND_COLLECTION_PERIOD_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(EXTEND_COLLECTION_PERIOD_ASYNC.CANCEL)))
        )
    )
  )

const resetParticipantDeadlinesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RESET_PARTICIPANT_DEADLINES_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .resetParticipantDeadlines(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          { includeIds: action.payload.includeIds }
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              RESET_PARTICIPANT_DEADLINES_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(formatResetDeadlineToast(response, { name: action.payload.name }))
              ),
              GET_CAMPAIGN_INFO_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_HEALTH_ASYNC.initiate({ id: action.payload.id })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(RESET_PARTICIPANT_DEADLINES_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(RESET_PARTICIPANT_DEADLINES_ASYNC.CANCEL)))
        )
    )
  )

const extendParticipantDeadlinesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(EXTEND_PARTICIPANT_DEADLINES_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .extendParticipantDeadlines(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          {
            includeIds: action.payload.includeIds,
            deadline: action.payload.deadline,
          }
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              EXTEND_PARTICIPANT_DEADLINES_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(
                  formatExtendDeadlineToast(response, {
                    name: action.payload.name,
                    deadline: action.payload.deadline,
                  })
                )
              ),
              GET_CAMPAIGN_INFO_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_HEALTH_ASYNC.initiate({ id: action.payload.id })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(EXTEND_PARTICIPANT_DEADLINES_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(EXTEND_PARTICIPANT_DEADLINES_ASYNC.CANCEL)))
        )
    )
  )

const createParticipantExclusionsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_PARTICIPANT_EXCLUSIONS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .createParticipantExclusions(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          { includeIds: action.payload.includeIds }
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              CREATE_PARTICIPANT_EXCLUSIONS_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(
                  formatParticipantExclusionToast(response, { name: action.payload.name })
                )
              ),
              GET_CAMPAIGN_INFO_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_HEALTH_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_STATUS_COUNTS_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_FINAL_RESULTS_ASYNC.initiate({ id: action.payload.id })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(CREATE_PARTICIPANT_EXCLUSIONS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(CREATE_PARTICIPANT_EXCLUSIONS_ASYNC.CANCEL)))
        )
    )
  )

const resetTrainingProgressEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RESET_TRAINING_PROGRESS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .resetTrainingProgress(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          {
            includeIds: action.payload.includeIds,
            reason: action.payload.reason,
          }
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              RESET_TRAINING_PROGRESS_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(
                  formatResetProgressToast(response, {
                    name: action.payload.name,
                    campaignState: action.payload.campaignState,
                  })
                )
              ),
              GET_CAMPAIGN_INFO_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_HEALTH_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_STATUS_COUNTS_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_FINAL_RESULTS_ASYNC.initiate({ id: action.payload.id })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(RESET_TRAINING_PROGRESS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(RESET_TRAINING_PROGRESS_ASYNC.CANCEL)))
        )
    )
  )

const createManualCompletionsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_MANUAL_COMPLETIONS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .createManualCompletions(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          {
            includeIds: action.payload.includeIds,
            reason: action.payload.reason,
          }
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              CREATE_MANUAL_COMPLETIONS_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(
                  formatManualCompletionToast(response, {
                    name: action.payload.name,
                  })
                )
              ),
              GET_CAMPAIGN_INFO_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_HEALTH_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_STATUS_COUNTS_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_FINAL_RESULTS_ASYNC.initiate({ id: action.payload.id })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(CREATE_MANUAL_COMPLETIONS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(CREATE_MANUAL_COMPLETIONS_ASYNC.CANCEL)))
        )
    )
  )

const revertManualCompletionsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REVERT_MANUAL_COMPLETIONS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .revertManualCompletions(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          {
            includeIds: action.payload.includeIds,
            reason: action.payload.reason,
          }
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              REVERT_MANUAL_COMPLETIONS_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(
                  formatManualCompletionRevertToast(response, {
                    name: action.payload.name,
                    campaignState: action.payload.campaignState,
                  })
                )
              ),
              GET_CAMPAIGN_INFO_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_HEALTH_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_STATUS_COUNTS_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC.initiate({ id: action.payload.id }),
              GET_CAMPAIGN_FINAL_RESULTS_ASYNC.initiate({ id: action.payload.id })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(REVERT_MANUAL_COMPLETIONS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(REVERT_MANUAL_COMPLETIONS_ASYNC.CANCEL)))
        )
    )
  )

const pauseParticipantRemindersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(PAUSE_PARTICIPANT_REMINDERS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .pauseParticipantReminders(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          {
            includeIds: action.payload.includeIds,
            pausedUntil: toPausedUntilIso(action.payload.pausedUntil),
          }
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              PAUSE_PARTICIPANT_REMINDERS_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(
                  formatPauseRemindersToast(response, {
                    name: action.payload.name,
                    pausedUntil: action.payload.pausedUntil,
                  })
                )
              )
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(PAUSE_PARTICIPANT_REMINDERS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(PAUSE_PARTICIPANT_REMINDERS_ASYNC.CANCEL)))
        )
    )
  )

const resumeParticipantRemindersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RESUME_PARTICIPANT_REMINDERS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .resumeParticipantReminders(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id,
          { includeIds: action.payload.includeIds }
        )
        .pipe(
          mergeMap(({ response }) =>
            of(
              RESUME_PARTICIPANT_REMINDERS_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(formatResumeRemindersToast(response, { name: action.payload.name }))
              )
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(RESUME_PARTICIPANT_REMINDERS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(RESUME_PARTICIPANT_REMINDERS_ASYNC.CANCEL)))
        )
    )
  )

const notifyManagersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(NOTIFY_MANAGERS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .notifyManagers(state$.value.partnerOptions.selectedCompany?.value, action.payload.id, {
          selectionData: {
            allItems: false,
            includeIds: action.payload.includeIds.map(String),
          },
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              NOTIFY_MANAGERS_ASYNC.onSuccess({ data: response }),
              addToast(
                reportToast(formatNotifyManagersToast(response, { name: action.payload.name }))
              )
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(NOTIFY_MANAGERS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(NOTIFY_MANAGERS_ASYNC.CANCEL)))
        )
    )
  )

const sendRemindersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SEND_REMINDERS_ASYNC.INITIATE),
    mergeMap((action) => {
      const includeIds = action.payload.includeIds
      const result = {
        sentCount: includeIds.length,
        name: action.payload.name,
      }

      return api.awarenessCenter
        .resendTraining(action.payload.id, {
          companyFilter: state$.value.partnerOptions.selectedCompany?.value,
          partner: !!selectPartner(state$.value),
          selectionData: {
            allItems: false,
            includeIds,
            excludeIds: [],
          },
        })
        .pipe(
          mergeMap(() =>
            of(
              SEND_REMINDERS_ASYNC.onSuccess({ data: result }),
              addToast(reportToast(formatSendReminderToast(result)))
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(SEND_REMINDERS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(SEND_REMINDERS_ASYNC.CANCEL)))
        )
    })
  )

const sendStatusRemindersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SEND_STATUS_REMINDERS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .resendTraining(action.payload.id, {
          companyFilter: state$.value.partnerOptions.selectedCompany?.value,
          partner: !!selectPartner(state$.value),
          status: action.payload.status,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              SEND_STATUS_REMINDERS_ASYNC.onSuccess({ data: response }),
              addToast(reportToast(formatStatusReminderToast(response)))
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(SEND_STATUS_REMINDERS_ASYNC.onError, error, [400, 404]),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(SEND_STATUS_REMINDERS_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignCompletionProgressEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignCompletionProgress(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id
        )
        .pipe(
          map((response) => GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_COMPLETION_PROGRESS_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignStatusCountsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_STATUS_COUNTS_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignStatusCounts(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id
        )
        .pipe(
          map((response) => GET_CAMPAIGN_STATUS_COUNTS_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_STATUS_COUNTS_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_STATUS_COUNTS_ASYNC.CANCEL)))
        )
    )
  )

const getCampaignStatusOverTimeEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .getCampaignStatusOverTime(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.id
        )
        .pipe(
          map((response) => GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC.onError, error, [400, 404]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_STATUS_OVER_TIME_ASYNC.CANCEL)))
        )
    )
  )

const createCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_DRAFT_ASYNC.INITIATE),
    mergeMap((action) => {
      const campaignData = selectData(state$.value)
      const now = new Date()
      const formattedDate = customFormat(now, 'yyMMdd_HHmmss')
      const campaignName = `Copy_Of_${campaignData.name}_${formattedDate}`
      const isAutomatedLevelBasedTraining = !campaignData.trainings?.length

      return api.wizardCampaign
        .saveCampaign(state$.value.partnerOptions.selectedCompany?.value, {
          defaultLocaleId:
            campaignData.locales[0]?.id || selectActualDefaultLanguage(state$.value).id,
          participantsChoices: {
            Names: action.payload.selection,
          },
          flowType: campaignData.flow_type,
          name: campaignName,
          deliveryType: MAP_DELIVERY_VALUE[campaignData.delivery_type],
          scheduleTime: customFormat(
            addHours(new Date(), HOURS_TO_START_CAMPAIGN),
            "yyyy-MM-dd'T'HH:mm"
          ),
          trainingIds: isAutomatedLevelBasedTraining
            ? null
            : campaignData.trainings.map(({ id }) => id),
          sendCampaignFailedEmail: campaignData.sendCampaignFailedEmail,
          sendCampaignSummaryToCampaignReportRecipients:
            campaignData.sendCampaignSummaryToCampaignReportRecipients,
          sendCompletionEmail: campaignData.sendCompletionEmail,
          sendRemindersUntil: new Date(
            addDaysToDate(new Date(), END_DATE_INTERVAL + DEFAULT_COLLECTION_PERIOD)
          ).toISOString(),
          trainingReminders: campaignData.trainingReminders,
          ...getCampaignDraft(campaignData),
        })
        .pipe(
          map(({ response }) => SAVE_DRAFT_ASYNC.onSuccess({ id: response.id })),
          catchError((error) => of(epicsErrorHandler(SAVE_DRAFT_ASYNC.onError, error, [400]))),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    })
  )

// Duplicate campaign - the same POST /campaign/:id/copy the campaigns list uses.
// The copy is a draft, and the report header opens it for editing, so the new id
// is what the success carries.
const duplicateCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DUPLICATE_CAMPAIGN_ASYNC.INITIATE),
    mergeMap((action) => {
      const companyId = state$.value.partnerOptions.selectedCompany?.value

      return api.awarenessCenter.copyCampaign(action.payload.id, companyId ? [companyId] : []).pipe(
        mergeMap(({ response }) => {
          const campaignId = response?.campaign_ids?.[0]
          const templatesWereCleared =
            response?.templates_cleared_campaign_ids?.includes(campaignId)

          return of(
            DUPLICATE_CAMPAIGN_ASYNC.onSuccess({ id: campaignId }),
            // the report unmounts on the redirect that follows, so its own toast
            // cannot carry this - the same card goes through ToastsContainer instead
            addToast(
              reportToast(
                templatesWereCleared
                  ? 'Campaign duplicated as a draft. Reselect templates before launching it.'
                  : 'Campaign duplicated as a draft.'
              )
            )
          )
        }),
        catchError((error) =>
          of(
            epicsErrorHandler(DUPLICATE_CAMPAIGN_ASYNC.onError, error),
            addToast({ message: extractErrorMessage(error), type: 'error' })
          )
        ),
        takeUntil(action$.pipe(ofType(DUPLICATE_CAMPAIGN_ASYNC.CANCEL)))
      )
    })
  )

const getCampaignDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_CAMPAIGN_DATA_ASYNC.INITIATE,
      RESEND_TRAINING_REQUEST_ASYNC.SUCCESS,
      SEGMENT_FILTER_CHANGE
    ),
    mergeMap((action) => {
      const id = action.payload.id || action.payload.campaignId

      return api.wizardCampaign
        .getCampaign(state$.value.partnerOptions.selectedCompany?.value, id)
        .pipe(
          mergeMap((response) => of(GET_CAMPAIGN_DATA_ASYNC.onSuccess({ data: response }))),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGN_DATA_ASYNC.onError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_DATA_ASYNC.CANCEL)))
        )
    })
  )

const CSV_ERROR_TOAST = 'The CSV could not be created. Try again.'

const readJobId = (response) => {
  const body = response?.response
  if (body && typeof body.jobId === 'string') {
    return of(body.jobId)
  }
  if (body && typeof body.job_id === 'string') {
    return of(body.job_id)
  }
  if (body && typeof body.text === 'function') {
    return from(body.text()).pipe(map((text) => JSON.parse(text).jobId))
  }
  if (typeof body === 'string') {
    return of(JSON.parse(body).jobId)
  }
  return throwError(() => ({ status: 500 }))
}

const saveDownloadBlob = (ajaxResponse, fallbackName) => {
  const filename = ajaxResponse.xhr
    ? xhrExtractFileName(ajaxResponse.xhr) || fallbackName
    : fallbackName
  download(
    ajaxResponse.response,
    filename,
    ajaxResponse.responseType || ajaxResponse.response?.type
  )
}

const waitForParticipantsCsvJob = (state$, api, campaignId, jobId) =>
  race(
    fromSocketEvent(PARTICIPANTS_CSV_READY).pipe(
      filter(
        (payload) =>
          String(payload?.jobId) === String(jobId) &&
          String(payload?.campaignId) === String(campaignId)
      ),
      take(1),
      mergeMap(() =>
        api.awarenessCenter.downloadParticipantsReportCsvFile(
          state$.value.partnerOptions.selectedCompany?.value,
          campaignId,
          jobId
        )
      ),
      map((zipResponse) => {
        saveDownloadBlob(zipResponse, `campaign_report_${campaignId}.zip`)
        return DOWNLOAD_PARTICIPANTS_CSV_ASYNC.onSuccess()
      })
    ),
    fromSocketEvent(PARTICIPANTS_CSV_ERROR).pipe(
      filter(
        (payload) =>
          String(payload?.jobId) === String(jobId) &&
          String(payload?.campaignId) === String(campaignId)
      ),
      take(1),
      mergeMap(() => throwError(() => ({ status: 500 })))
    )
  )

const downloadParticipantsCsvEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DOWNLOAD_PARTICIPANTS_CSV_ASYNC.INITIATE),
    mergeMap((action) => {
      const campaignId = action.payload.id
      return api.awarenessCenter
        .downloadParticipantsReportCsv(
          state$.value.partnerOptions.selectedCompany?.value,
          campaignId,
          action.payload.body
        )
        .pipe(
          mergeMap((response) => {
            if (response.status === 202) {
              return readJobId(response).pipe(
                mergeMap((jobId) => waitForParticipantsCsvJob(state$, api, campaignId, jobId))
              )
            }
            saveDownloadBlob(response, `campaign_report_${campaignId}.csv`)
            return of(DOWNLOAD_PARTICIPANTS_CSV_ASYNC.onSuccess())
          }),
          catchError((error) =>
            of(
              epicsErrorHandler(DOWNLOAD_PARTICIPANTS_CSV_ASYNC.onError, error, [400, 404]),
              addToast(reportToast(CSV_ERROR_TOAST))
            )
          ),
          takeUntil(action$.pipe(ofType(DOWNLOAD_PARTICIPANTS_CSV_ASYNC.CANCEL)))
        )
    })
  )

const shareCsvReportEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SHARE_CSV_REPORT_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .shareCsvReport(action.payload.id, {
          search: selectFilters(state$.value).search,
          simulationResultTypes: selectFilters(state$.value).simulationResultTypes,
          trainingResultTypes: selectFilters(state$.value).trainingResultTypes,
          departments: selectFilters(state$.value).departments,
          trainingScores: selectFilters(state$.value).trainingResults,
          awarenessLevels: selectFilters(state$.value).levels,
          scenarios: selectFilters(state$.value).scenarios,
          segments: selectSegment(state$.value),
          action: SHARE_ACTIONS.SHARE_WIA_EMAIL,
          shareData: {
            emails: selectSelectedProfilesProperties(state$.value),
            message: selectMessage(state$.value),
          },
        })
        .pipe(
          mergeMap(() => of(addToast({ message: 'The Report has been sent' }), shareReset())),
          catchError((error) =>
            of(
              epicsErrorHandler(SHARE_CSV_REPORT_ASYNC.onError, error),
              addToast({ message: error.response?.message, type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(SHARE_CSV_REPORT_ASYNC.CANCEL)))
        )
    )
  )

const saveManualTrainingResultsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_MANUAL_TRAINING_RESULTS_ASYNC.INITIATE),
    mergeMap((action) => {
      const selectionData = selectSelectionData(state$.value)

      return api.awarenessCenter
        .saveManualTrainingResults(action.payload.id, {
          search: selectFilters(state$.value).search,
          simulationResultTypes: selectFilters(state$.value).simulationResultTypes,
          trainingResultTypes: selectFilters(state$.value).trainingResultTypes,
          departments: selectFilters(state$.value).departments,
          trainingScores: selectFilters(state$.value).trainingResults,
          awarenessLevels: selectFilters(state$.value).levels,
          scenarios: selectFilters(state$.value).scenarios,
          segments: selectSegment(state$.value),
          status: selectTrainingResultsModalStatus(state$.value),
          customPostponedDate: selectTrainingResultsPostponedDate(state$.value),
          selectionData: selectionData.allItems
            ? {
                allItems: true,
                excludeIds: selectionData.selectionIds,
              }
            : {
                allItems: false,
                includeIds: selectionData.selectionIds,
                excludeIds: [],
              },
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              SAVE_MANUAL_TRAINING_RESULTS_ASYNC.onSuccess(),
              addToast({
                message: `Training status for ${response.count} participants has been successfully changed. `,
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(SAVE_MANUAL_TRAINING_RESULTS_ASYNC.onError, error),
              addToast({ message: extractErrorMessage(error.response?.message), type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(SAVE_MANUAL_TRAINING_RESULTS_ASYNC.CANCEL)))
        )
    })
  )

export default combineEpics(
  emergencyStopEpic,
  resendTrainingEpic,
  getReportParticipantsListEpic,
  getReportSummaryEpic,
  getCampaignHealthEpic,
  getCampaignStatusCountsEpic,
  getCampaignStatusOverTimeEpic,
  getCampaignCompletionProgressEpic,
  getCampaignInfoEpic,
  getCampaignSimulationOverviewEpic,
  getCampaignSimulationFunnelEpic,
  getCampaignDepartmentOutcomesEpic,
  getCampaignFinalResultsEpic,
  getCampaignParticipantsEpic,
  createAssignmentResendsEpic,
  extendCollectionPeriodEpic,
  resetParticipantDeadlinesEpic,
  extendParticipantDeadlinesEpic,
  createParticipantExclusionsEpic,
  resetTrainingProgressEpic,
  createManualCompletionsEpic,
  revertManualCompletionsEpic,
  pauseParticipantRemindersEpic,
  resumeParticipantRemindersEpic,
  notifyManagersEpic,
  sendRemindersEpic,
  sendStatusRemindersEpic,
  createCampaignEpic,
  duplicateCampaignEpic,
  getCampaignDataEpic,
  downloadParticipantsCsvEpic,
  shareCsvReportEpic,
  saveManualTrainingResultsEpic
)
